import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';

/**
 * «⋯» PÅ EN RAD — `ux-flow-architect` «Meny fra en knapp», per rad i stedet for i skallet.
 *
 * Kravene er skillets, og de er krav og ikke smak:
 *   - `role="dialog"` UTEN `aria-modal`, ingen fokusfelle, ingen scrim. Siden bak er i bruk.
 *   - `aria-expanded` på utløseren bærer tilstanden.
 *   - Esc og pekertrykk utenfor lukker, og fokus går tilbake til «⋯».
 *   - Fokus flyttes INN ved åpning, ellers er menyen utastbar.
 *   - Sidens taster blokkeres IKKE: menyen er et sted å velge, ikke en oppgave.
 *
 * FORSKJELLEN FRA SKALLETS MENY er hvor den står. Skallets ligger i et hjørne og kan være
 * `position: absolute` i `.panel`. Denne hører til en RAD, og raden ligger i en flate som
 * ruller (`.panel-innst` er `overflow-y: auto`) — en absolutt plassert meny ville blitt
 * klippet av rullefeltet, som er nøyaktig feilen skallets meny hadde (avvik 14). Den er
 * derfor `position: fixed` og festet til utløserens plass i VINDUET, målt ved åpning.
 * Rulling flytter utløseren under menyen, så rulling lukker den — å flytte en åpen meny
 * etter et element som beveger seg er mer maskineri enn valget er verdt.
 *
 * MÅLINGEN SETTES SOM CSS-VARIABLER og ikke som `top`/`right` direkte: en piksel her er en
 * MÅLT posisjon i vinduet og ikke en designverdi, så den kan ikke komme fra temaet — men
 * ved å gå veien om en variabel blir regelen som bruker den stående i CSS-filen, der
 * resten av menyens utseende bor. Samme grep som statusfargen i kjentefeil-raden.
 *
 * TOM MENY FINNES IKKE: kalleren sender ingen `Radmeny` når raden ikke har et valg. En «⋯»
 * som åpner ingenting er et løfte som ikke holdes.
 */
export interface Radvalg {
  navn: string;
  ikon: string;
  /** Handlingen fjerner noe hos leverandøren og kan ikke angres — rød tekst (DD-53). */
  fare?: boolean;
  onVelg: () => void;
  testid?: string;
}

export function Radmeny({ etikett, testid, valg }: { etikett: string; testid: string; valg: Radvalg[] }) {
  const smal = useSmal();
  const [aapen, setAapen] = useState(false);
  const [plass, setPlass] = useState<CSSProperties>({});
  const knapp = useRef<HTMLButtonElement>(null);
  const rot = useRef<HTMLDivElement>(null);

  /** Lukker og gir fokus tilbake til «⋯» — begge veier ett sted, som i skallet. */
  const lukk = useCallback(() => {
    setAapen(false);
    knapp.current?.focus();
  }, []);

  const aapne = () => {
    const r = knapp.current?.getBoundingClientRect();
    if (r) {
      setPlass({
        '--radmeny-topp': `${Math.round(r.bottom)}px`,
        '--radmeny-hoeyre': `${Math.round(window.innerWidth - r.right)}px`,
      } as CSSProperties);
    }
    setAapen(true);
  };

  // Fokus INN ved åpning. `aapen` i deps, ikke tom liste: menyen monteres ikke på nytt.
  useEffect(() => {
    if (aapen) rot.current?.querySelector<HTMLElement>('button:not([disabled])')?.focus();
  }, [aapen]);

  useEffect(() => {
    if (!aapen) return undefined;
    // Sammenligningen er mot DENNE menyen, ikke mot enhver `[data-radmeny]`: å åpne raden
    // under skal lukke raden over, og det skjer av seg selv når hver meny bare kjenner sin egen.
    const ned = (e: PointerEvent) => {
      const el = e.target instanceof Element ? e.target : null;
      if (el && (el.closest('[data-radmeny]') === rot.current || el.closest('[data-radmeny-knapp]') === knapp.current)) return;
      setAapen(false);
    };
    const tast = (e: KeyboardEvent) => {
      if (e.key === 'Escape') lukk();
    };
    // Capture på rulling: flaten som ruller er `.panel-innst`, ikke vinduet, og en
    // boble-lytter på `window` ville aldri sett den.
    const rullet = () => setAapen(false);
    document.addEventListener('pointerdown', ned);
    document.addEventListener('keydown', tast);
    window.addEventListener('scroll', rullet, true);
    window.addEventListener('resize', rullet);
    return () => {
      document.removeEventListener('pointerdown', ned);
      document.removeEventListener('keydown', tast);
      window.removeEventListener('scroll', rullet, true);
      window.removeEventListener('resize', rullet);
    };
  }, [aapen, lukk]);

  return (
    <>
      <button
        ref={knapp}
        type="button"
        data-radmeny-knapp=""
        className={aapen ? 'panel-mer panel-mer-aapen' : 'panel-mer'}
        aria-label={`Mer for ${etikett}`}
        aria-expanded={aapen}
        onClick={() => (aapen ? lukk() : aapne())}
        data-testid={`mer-${testid}`}
      >
        <Ikon navn="ellipsis-horizontal-outline" />
      </button>

      {aapen ? (
        <div
          ref={rot}
          data-radmeny=""
          role="dialog"
          aria-label={`Mer for ${etikett}`}
          className={smal ? 'panel-meny panel-radmeny-ark' : 'panel-meny panel-radmeny'}
          style={plass}
          data-testid={`radmeny-${testid}`}
        >
          {valg.map((v) => (
            <button
              key={v.navn}
              type="button"
              className={v.fare ? 'panel-meny-rad panel-meny-rad-fare' : 'panel-meny-rad'}
              onClick={() => {
                setAapen(false);
                v.onVelg();
              }}
              data-testid={v.testid}
            >
              <Ikon navn={v.ikon} />
              {v.navn}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
